import { yupResolver } from "@hookform/resolvers/yup";
import {
    setSustainability,
    StepperType,
    stepperTypeSelector,
    sustainabilityIdSelector,
    useAppDispatch,
    useAppSelector,
} from "@repo/logic";
import { useEffect, useMemo } from "react";
import { FormProvider, useForm } from "react-hook-form";
import * as Yup from "yup";
import { useTypedTranslation } from "../../hooks/useTypedTranslation";
import { RadioInput } from "../Form";
import { RadioGroup } from "../Form/RadioGroup";
import { DefaultStepProps } from "../Stepper/CustomStepper";
import * as Styled from "./SustainabilityPage.styled";

type SustainabilityOption = {
    id: string;
    label: string;
};

type FormValues = {
    sustainabilityPreference: string;
};

export const SustainabilityPage: React.FC<DefaultStepProps> = ({ setContinueCondition, setModalTitle }) => {
    const sustainabilityId = useAppSelector(sustainabilityIdSelector);
    const { t } = useTypedTranslation();
    const dispatch = useAppDispatch();
    const stepperType = useAppSelector(stepperTypeSelector);

    // Set modal title if changing pension contribution
    useEffect(() => {
        if (stepperType === StepperType.ChangePensionContribution)
            setModalTitle?.(t("investmentStrategy.toChangePensionQuestionnaire"));
    }, [setModalTitle, t, stepperType]);

    const validationSchema = useMemo(
        () =>
            Yup.object<FormValues>().shape({
                sustainabilityPreference: Yup.string()
                    .required(t("investmentStrategy.sfdr.noSustainabilitySelected"))
                    .test(
                        "no-sustainable-funds-available",
                        t("investmentStrategy.sfdr.noSustainableFundsAvailable"),
                        (value) => value !== "yes_not_willing_non_sustainable",
                    ),
            }),
        [t],
    );

    const methods = useForm<FormValues>({
        defaultValues: { sustainabilityPreference: sustainabilityId },
        resolver: yupResolver(validationSchema),
        mode: "onChange",
    });
    const { watch, setValue, trigger } = methods;

    const OPTIONS: SustainabilityOption[] = useMemo(
        () => [
            {
                id: "yes_not_willing_non_sustainable",
                label: t("investmentStrategy.sfdr.yes"),
            },
            {
                id: "partialYes_willing_non_sustainable",
                label: t("investmentStrategy.sfdr.partialYes"),
            },
            {
                id: "no_not_interested",
                label: t("investmentStrategy.sfdr.no"),
            },
        ],
        [t],
    );

    // watch selected value
    const selected = watch("sustainabilityPreference");

    // If redux already has a value and RHF hasn't been hydrated (e.g. user navigated back), ensure form reflects it
    useEffect(() => {
        if (!selected && sustainabilityId) {
            setValue("sustainabilityPreference", sustainabilityId);
        }
    }, [sustainabilityId, selected, setValue]);

    // Register continue condition so step cannot advance without selection
    useEffect(() => {
        setContinueCondition(async () => {
            const isValid = await trigger();
            if (isValid) {
                dispatch(setSustainability(selected));
            }
            return isValid;
        });
    }, [dispatch, selected, setContinueCondition, trigger]);

    return (
        <FormProvider {...methods}>
            <form>
                <Styled.Wrapper>
                    <Styled.Title>{t("investmentStrategy.sfdr.sustainability")}</Styled.Title>
                    <Styled.Paragraph>{t("investmentStrategy.sfdr.questionnaireQuestion")}</Styled.Paragraph>
                    <Styled.RadioGroupWrapper>
                        <RadioGroup name="sustainabilityPreference">
                            {OPTIONS.map((opt) => (
                                <RadioInput
                                    id={opt.id}
                                    key={opt.id}
                                    name="sustainabilityPreference"
                                    label={opt.label}
                                    value={opt.id}
                                />
                            ))}
                        </RadioGroup>
                    </Styled.RadioGroupWrapper>
                </Styled.Wrapper>
            </form>
        </FormProvider>
    );
};
